// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  Link,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { usePermissions } from "../../hooks";
import { useTranslate } from "@refinedev/core";
import { apiVersion, apiJsonDedup } from "../../utils/api";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { trackViewTransactionList, trackViewTransaction, trackBillingFilterApplied } from "../../utils/analytics-events";

export type PaymentTransaction = {
  id: string;
  amount: number;
  currency: string;
  status?: string;
  payment?: {
    method?: string;
    brand?: string;
    last4?: string;
    type?: string;
    receipt_number?: string;
    receipt_url?: string;
  } | null;
  payment_method_type?: string;
  created?: number;
  property?: {
    id: string | null;
    name?: string | null;
    property_id?: string | null;
  } | null;
  profile?: {
    id?: string;
    name?: string;
    email?: string;
  } | null;
};

type CursorPaginatedResponse<T> = {
  data: T[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasMore: boolean;
  };
};

const formatAmount = (amount: number | undefined, currency: string | undefined) => {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "-";
  const divisor = 100; // Stripe-style smallest currency unit
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(amount / divisor);
  } catch {
    return `${(amount / divisor).toFixed(2)} ${currency || ""}`.trim();
  }
};

const formatDateTime = (value?: number) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusColor = (status?: string) => {
  if (!status) return "default" as const;
  const normalized = status.toLowerCase();
  if (["succeeded", "paid", "succeeded"].includes(normalized)) return "success" as const;
  if (["requires_payment_method", "requires_confirmation", "processing"].includes(normalized)) return "warning" as const;
  if (["canceled", "failed"].includes(normalized)) return "error" as const;
  return "default" as const;
};

const formatPaymentMethod = (payment?: PaymentTransaction['payment']) => {
  if (!payment) return "-";
  
  const parts: string[] = [];
  
  if (payment.brand) {
    parts.push(payment.brand.charAt(0).toUpperCase() + payment.brand.slice(1));
  }
  
  if (payment.last4) {
    parts.push(`••••${payment.last4}`);
  }
  
  if (payment.type) {
    parts.push(`(${payment.type})`);
  }
  
  return parts.length > 0 ? parts.join(' ') : '-';
};

const surfaceSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  boxShadow: (theme: any) => theme.shadows[1],
};

export const BillingTransactionsPage: React.FC = () => {
  const { hasFullAccess } = usePermissions();
  const t = useTranslate();
  const [items, setItems] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(10);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const currentPage = cursorStack.length;

  const fetchData = useCallback(async (cursor: string | undefined, pageLimit: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageLimit));
      if (cursor) params.set('cursor', cursor);
      
      const response = await apiJsonDedup<CursorPaginatedResponse<PaymentTransaction>>(
        `admin/${apiVersion}/billing/transactions?${params.toString()}`
      );
      
      setItems(response.data || []);
      setNextCursor(response.pagination?.nextCursor);
      setHasMore(response.pagination?.hasMore || false);
      
      // Track transaction list view
      const totalValue = response.data?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
      trackViewTransactionList(response.data?.length, totalValue, 'USD');
    } catch (e: any) {
      const errorMessage = e?.message ?? "Failed to load transactions";
      setError(errorMessage);
      setItems([]);
      setHasMore(false);
      // Capture billing error with critical priority
      Sentry.captureException(e, {
        tags: {
          priority: "critical",
          context: "billing",
          action: "fetch_transactions",
        },
        extra: {
          pageLimit,
          cursor,
          errorMessage,
        },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const currentCursor = cursorStack[cursorStack.length - 1];
    void fetchData(currentCursor, limit);
  }, [cursorStack, limit, fetchData]);

  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      setCursorStack(prev => [...prev, nextCursor]);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 1) {
      setCursorStack(prev => prev.slice(0, -1));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCursorStack([undefined]);
  };

  const showProfileColumn = hasFullAccess;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", p: 2 }}>
      <Box
        sx={{
          ...surfaceSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreditCardIcon fontSize="medium" /> {t("billing.transactions.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("billing.transactions.subtitle")}
          </Typography>
        </Box>
      </Box>

      <Card sx={surfaceSx}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography color="text.secondary">No transactions found</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("billing.transactions.table.receiptNumber")}</TableCell>
                    <TableCell>{t("billing.transactions.table.timestamp")}</TableCell>
                    <TableCell>{t("billing.transactions.table.amount")}</TableCell>
                    <TableCell>{t("billing.transactions.table.status")}</TableCell>
                    <TableCell>{t("billing.transactions.table.paymentMethod")}</TableCell>
                    <TableCell>{t("billing.transactions.table.property")}</TableCell>
                    {showProfileColumn && (
                      <>
                        <TableCell>{t("billing.transactions.table.profileName")}</TableCell>
                        <TableCell>{t("billing.transactions.table.profileEmail")}</TableCell>
                      </>
                    )}
                    <TableCell>{t("billing.transactions.table.receipt")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((tx) => (
                    <TableRow
                      key={tx.id}
                      hover
                      sx={{ "&:last-of-type td": { borderBottom: 0 }, "& td": { py: 2 }, cursor: 'pointer' }}
                      onClick={() => {
                        // Track transaction view
                        trackViewTransaction(tx.id, tx.amount || 0, tx.currency || 'USD', tx.status, undefined, tx.property?.property_id);
                      }}
                    >
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {tx.payment?.receipt_number || "-"}
                      </TableCell>
                      <TableCell>{formatDateTime(tx.created)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatAmount(tx.amount, tx.currency)}</TableCell>
                      <TableCell>
                        {tx.status ? (
                          <Chip
                            size="small"
                            label={tx.status}
                            color={statusColor(tx.status)}
                            variant={statusColor(tx.status) === "default" ? "outlined" : "filled"}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{formatPaymentMethod(tx.payment)}</TableCell>
                      <TableCell>
                        {tx.property?.property_id ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <Typography variant="body2" fontWeight={600}>
                              <a href={`/properties/${tx.property.property_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {tx.property.name || "-"}
                              </a>
                            </Typography>
                          </Box>
                        ) : (
                          tx.property?.name || "-"
                        )}
                      </TableCell>
                      {showProfileColumn && (
                        <>
                          <TableCell>
                            {tx.profile?.name || "-"}
                          </TableCell>
                          <TableCell>
                            {tx.profile?.email || "-"}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        {tx.payment?.receipt_url ? (
                          <Link
                            href={tx.payment.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none' }}
                          >
                            Receipt
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Page {currentPage}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Size</InputLabel>
            <Select
              value={limit}
              label="Size"
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              sx={{ '& .MuiSelect-select': { textAlign: 'center', paddingRight: '24px' } }}
            >
              {[5, 10, 15, 20].map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ justifyContent: 'center' }}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              startIcon={<NavigateBeforeIcon />}
            >
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleNextPage}
              disabled={!hasMore}
              endIcon={<NavigateNextIcon />}
            >
              Next
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default BillingTransactionsPage;
